import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { CommentOutlined, DeleteOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { useHistory } from 'react-router-dom';
import {
  useCreateTelegramMessageCommentMutation,
  useDeleteTelegramMessageCommentMutation,
  useGetTelegramMessageCommentsQuery,
} from 'graphql/types';
import {
  CommentAuthor,
  CommentContent,
  CommentDeleteButton,
  CommentForm,
  CommentFormActions,
  CommentItem,
  CommentItemHeader,
  CommentItemMeta,
  CommentList,
  CommentLoading,
  CommentLoginHint,
  CommentSection,
  CommentSubmitButton,
  CommentTextarea,
  CommentTime,
  CommentToggleButton,
} from './index.styled';
import { MessageComment } from './types';
import { isRtlText } from 'utils/textDirection';

type Props = {
  messageId: string;
  commentsCount?: number;
  isLoggedIn: boolean;
  currentUserId?: string;
  onCommentsCountChange?: (count: number) => void;
};

export default function MessageComments({
  messageId,
  commentsCount = 0,
  isLoggedIn,
  currentUserId,
  onCommentsCountChange,
}: Props) {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');

  const { data, loading, refetch } = useGetTelegramMessageCommentsQuery({
    variables: {
      messageId,
      sort: ['createdAt:asc'],
      pagination: { page: 1, pageSize: 100 },
    },
    skip: !expanded || !messageId,
    fetchPolicy: 'network-only',
  });

  const comments = useMemo<MessageComment[]>(
    () =>
      (data?.telegramMessageComments?.data ?? []).flatMap((entity) => {
        if (!entity.id) return [];
        return [
          {
            id: String(entity.id),
            content: entity.attributes?.content?.trim() || '',
            authorName: entity.attributes?.authorName?.trim() || 'User',
            authorId: entity.attributes?.author?.data?.id
              ? String(entity.attributes.author.data.id)
              : undefined,
            createdAt: entity.attributes?.createdAt || '',
          },
        ];
      }),
    [data],
  );

  const total = data?.telegramMessageComments?.meta?.pagination?.total ?? comments.length;
  const displayCount = expanded ? total : commentsCount;

  const [createComment, { loading: creating }] = useCreateTelegramMessageCommentMutation();
  const [deleteComment, { loading: deleting }] = useDeleteTelegramMessageCommentMutation();

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;

    if (!isLoggedIn) {
      history.push('/login');
      return;
    }

    try {
      await createComment({
        variables: {
          messageId,
          input: { content },
        },
      });
      setDraft('');
      const result = await refetch();
      const newTotal =
        result.data?.telegramMessageComments?.meta?.pagination?.total ?? total + 1;
      onCommentsCountChange?.(newTotal);
      message.success('Comment added');
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment({ variables: { commentId } });
      const result = await refetch();
      const newTotal =
        result.data?.telegramMessageComments?.meta?.pagination?.total ?? Math.max(0, total - 1);
      onCommentsCountChange?.(newTotal);
      message.success('Comment deleted');
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  return (
    <CommentSection>
      <CommentToggleButton type="button" onClick={handleToggle} aria-expanded={expanded}>
        <CommentOutlined />
        {displayCount > 0 ? `${displayCount} comment${displayCount === 1 ? '' : 's'}` : 'Comment'}
      </CommentToggleButton>

      {expanded && (
        <>
          {loading ? (
            <CommentLoading>Loading comments...</CommentLoading>
          ) : (
            <>
              {comments.length > 0 && (
                <CommentList>
                  {comments.map((comment) => {
                    const canDelete = Boolean(currentUserId && comment.authorId === currentUserId);
                    return (
                      <CommentItem key={comment.id}>
                        <CommentItemHeader>
                          <CommentAuthor>{comment.authorName}</CommentAuthor>
                          <CommentItemMeta>
                            <CommentTime>
                              {comment.createdAt ? moment(comment.createdAt).fromNow() : ''}
                            </CommentTime>
                            {canDelete && (
                              <CommentDeleteButton
                                type="button"
                                disabled={deleting}
                                aria-label="Delete comment"
                                title="Delete comment"
                                onClick={() => handleDelete(comment.id)}
                              >
                                <DeleteOutlined />
                              </CommentDeleteButton>
                            )}
                          </CommentItemMeta>
                        </CommentItemHeader>
                        <CommentContent $rtl={isRtlText(comment.content)}>{comment.content}</CommentContent>
                      </CommentItem>
                    );
                  })}
                </CommentList>
              )}

              {isLoggedIn ? (
                <CommentForm onSubmit={handleSubmit}>
                  <CommentTextarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Write a comment..."
                    disabled={creating}
                  />
                  <CommentFormActions>
                    <CommentSubmitButton type="submit" disabled={creating || !draft.trim()}>
                      {creating ? 'Posting...' : 'Post comment'}
                    </CommentSubmitButton>
                  </CommentFormActions>
                </CommentForm>
              ) : (
                <CommentLoginHint>
                  <button type="button" onClick={() => history.push('/login')}>
                    Sign in
                  </button>{' '}
                  to add a comment.
                </CommentLoginHint>
              )}
            </>
          )}
        </>
      )}
    </CommentSection>
  );
}
